import { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Space, Upload, message, Breadcrumb, Modal, Form, Input,
  Typography, Card, Dropdown, Popconfirm, Progress, Tooltip, Tag,
} from 'antd';
import {
  CloudServerOutlined, FolderOutlined, FileOutlined, UploadOutlined,
  DownloadOutlined, DeleteOutlined, EditOutlined, ShareAltOutlined,
  SendOutlined, ReloadOutlined, PlusOutlined, HomeOutlined,
  LinkOutlined, CopyOutlined, MoreOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ExchangeService } from '../api/services/exchange.service';
import { ChatService } from '../api/services/chat.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export default function ExchangePage() {
  const [credentials, setCredentials] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [mkdirModalOpen, setMkdirModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareToChatModalOpen, setShareToChatModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [setupForm] = Form.useForm();
  const [mkdirForm] = Form.useForm();
  const [renameForm] = Form.useForm();
  const [shareForm] = Form.useForm();

  useEffect(() => {
    loadCredentials();
  }, []);

  useEffect(() => {
    if (credentials?.configured) {
      loadFiles(currentPath);
    }
  }, [credentials, currentPath]);

  const loadCredentials = async () => {
    try {
      const res = await ExchangeService.getCredentials();
      setCredentials(res.data);
    } catch (err) {
      message.error('Ошибка загрузки настроек');
    }
  };

  const loadFiles = async (path) => {
    setLoading(true);
    try {
      const res = await ExchangeService.listFiles(path);
      setFiles(res.data);
      setCurrentPath(path);
    } catch (err) {
      message.error('Ошибка загрузки файлов');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (values) => {
    try {
      await ExchangeService.setCredentials(values.nc_username, values.nc_password);
      message.success('Учётные данные сохранены');
      await loadCredentials();
    } catch (err) {
      message.error('Ошибка сохранения');
    }
  };

  const handleUpload = async (info) => {
    const file = info.file;
    const path = `${currentPath === '/' ? '' : currentPath}/${file.name}`;
    
    try {
      await ExchangeService.uploadFile(path, file);
      message.success(`Файл "${file.name}" загружен`);
      loadFiles(currentPath);
    } catch (err) {
      message.error(`Ошибка загрузки файла "${file.name}"`);
    }
  };

  const handleDownload = async (file) => {
    try {
      const res = await ExchangeService.downloadFile(file.path);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Ошибка скачивания');
    }
  };

  const handleDelete = async (file) => {
    try {
      await ExchangeService.deleteItem(file.path);
      message.success(`"${file.name}" удалён`);
      loadFiles(currentPath);
    } catch (err) {
      message.error('Ошибка удаления');
    }
  };

  const handleMkdir = async (values) => {
    const path = `${currentPath === '/' ? '' : currentPath}/${values.name}`;
    try {
      await ExchangeService.createFolder(path);
      message.success(`Папка "${values.name}" создана`);
      setMkdirModalOpen(false);
      mkdirForm.resetFields();
      loadFiles(currentPath);
    } catch (err) {
      message.error('Ошибка создания папки');
    }
  };

  const handleRename = async (values) => {
    const parentPath = currentPath === '/' ? '/' : currentPath;
    const newPath = `${parentPath === '/' ? '' : parentPath}/${values.name}`;
    try {
      await ExchangeService.moveItem(selectedFile.path, newPath);
      message.success('Переименовано');
      setRenameModalOpen(false);
      renameForm.resetFields();
      loadFiles(currentPath);
    } catch (err) {
      message.error('Ошибка переименования');
    }
  };

  const handleCreateShare = async () => {
    try {
      const res = await ExchangeService.createShare(selectedFile.path);
      setShareUrl(res.data.url);
      message.success('Ссылка создана');
    } catch (err) {
      message.error('Ошибка создания ссылки');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    message.success('Ссылка скопирована');
  };

  const handleShareToChat = async () => {
    if (!selectedRoomId) {
      message.error('Выберите чат');
      return;
    }
    try {
      await ExchangeService.shareToChat(selectedFile.path, selectedRoomId);
      message.success('Файл отправлен в чат');
      setShareToChatModalOpen(false);
      setSelectedRoomId(null);
    } catch (err) {
      message.error('Ошибка отправки в чат');
    }
  };

  const openShareToChat = async () => {
    try {
      const res = await ChatService.getRooms();
      setChatRooms(res.data);
      setShareToChatModalOpen(true);
    } catch (err) {
      message.error('Ошибка загрузки чатов');
    }
  };

  const handleOpenInNc = async (file) => {
    try {
      const res = await ExchangeService.openInNc(file.path);
      window.open(res.data.url, '_blank');
    } catch (err) {
      message.error('Ошибка открытия');
    }
  };

  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean);
    const items = [{ title: <HomeOutlined />, path: '/' }];
    let path = '';
    parts.forEach((part, index) => {
      path += '/' + part;
      items.push({
        title: part,
        path: path,
        onClick: () => loadFiles(path),
      });
    });
    return items;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' Б';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' КБ';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' МБ';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' ГБ';
  };

  const getContextMenuItems = (file) => [
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: 'Скачать',
      onClick: () => handleDownload(file),
    },
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: 'Переименовать',
      onClick: () => {
        setSelectedFile(file);
        renameForm.setFieldsValue({ name: file.name });
        setRenameModalOpen(true);
      },
    },
    {
      key: 'share',
      icon: <LinkOutlined />,
      label: 'Публичная ссылка',
      onClick: () => {
        setSelectedFile(file);
        setShareUrl('');
        setShareModalOpen(true);
      },
    },
    {
      key: 'send-to-chat',
      icon: <SendOutlined />,
      label: 'Отправить в чат',
      onClick: () => {
        setSelectedFile(file);
        openShareToChat();
      },
    },
    {
      key: 'open-in-nc',
      icon: <CloudServerOutlined />,
      label: 'Открыть в Nextcloud',
      onClick: () => handleOpenInNc(file),
    },
    { type: 'divider' },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Удалить',
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: 'Удалить?',
          icon: <ExclamationCircleOutlined />,
          content: `"${file.name}" будет удалён безвозвратно`,
          okText: 'Удалить',
          okType: 'danger',
          cancelText: 'Отмена',
          onOk: () => handleDelete(file),
        });
      },
    },
  ];

  const columns = [
    {
      title: 'Имя',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {record.is_dir ? (
            <FolderOutlined style={{ color: '#faad14', fontSize: 18 }} />
          ) : (
            <FileOutlined style={{ color: '#1890ff', fontSize: 18 }} />
          )}
          <span
            style={{ cursor: record.is_dir ? 'pointer' : 'default' }}
            onClick={() => record.is_dir && loadFiles(record.path)}
          >
            {name}
          </span>
        </Space>
      ),
    },
    {
      title: 'Размер',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size, record) => record.is_dir ? '—' : formatSize(size),
    },
    {
      title: 'Изменён',
      dataIndex: 'last_modified',
      key: 'last_modified',
      width: 180,
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Dropdown menu={{ items: getContextMenuItems(record) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  if (!credentials) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CloudServerOutlined style={{ fontSize: 28, color: '#6366f1' }} />
            <div>
              <h1 className="page-title" style={{ margin: 0 }}>Обменник</h1>
              <p className="page-subtitle" style={{ margin: 0 }}>Файловое хранилище Nextcloud</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!credentials.configured) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CloudServerOutlined style={{ fontSize: 28, color: '#6366f1' }} />
            <div>
              <h1 className="page-title" style={{ margin: 0 }}>Обменник</h1>
              <p className="page-subtitle" style={{ margin: 0 }}>Файловое хранилище Nextcloud</p>
            </div>
          </div>
        </div>

        <Card style={{ maxWidth: 480, margin: '60px auto' }}>
          <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>
            Настройка подключения к Nextcloud
          </Title>
          <Form form={setupForm} layout="vertical" onFinish={handleSetup}>
            <Form.Item
              name="nc_username"
              label="Логин Nextcloud"
              rules={[{ required: true, message: 'Введите логин' }]}
            >
              <Input placeholder="username" />
            </Form.Item>
            <Form.Item
              name="nc_password"
              label="Пароль Nextcloud"
              rules={[{ required: true, message: 'Введите пароль' }]}
            >
              <Input.Password placeholder="Пароль" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  border: 'none',
                }}
              >
                Подключиться
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CloudServerOutlined style={{ fontSize: 28, color: '#6366f1' }} />
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Обменник</h1>
            <Breadcrumb items={getBreadcrumbs()} style={{ marginTop: 4 }} />
          </div>
        </div>
        <Space>
          <Tooltip title="Обновить">
            <Button icon={<ReloadOutlined />} onClick={() => loadFiles(currentPath)} />
          </Tooltip>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setMkdirModalOpen(true)}
          >
            Папка
          </Button>
          <Upload
            customRequest={({ file, onSuccess, onError }) => {
              handleUpload({ file }).then(onSuccess).catch(onError);
            }}
            showUploadList={false}
            multiple
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                border: 'none',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
              }}
            >
              Загрузить
            </Button>
          </Upload>
        </Space>
      </div>

      <Table
        dataSource={files}
        columns={columns}
        rowKey="path"
        loading={loading}
        pagination={false}
        size="middle"
        locale={{ emptyText: 'Папка пуста' }}
      />

      {/* Модальное окно: Создание папки */}
      <Modal
        title="Новая папка"
        open={mkdirModalOpen}
        onCancel={() => { setMkdirModalOpen(false); mkdirForm.resetFields(); }}
        onOk={() => mkdirForm.submit()}
        okText="Создать"
      >
        <Form form={mkdirForm} layout="vertical" onFinish={handleMkdir}>
          <Form.Item
            name="name"
            label="Имя папки"
            rules={[{ required: true, message: 'Введите имя папки' }]}
          >
            <Input placeholder="Новая папка" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно: Переименование */}
      <Modal
        title="Переименовать"
        open={renameModalOpen}
        onCancel={() => { setRenameModalOpen(false); renameForm.resetFields(); }}
        onOk={() => renameForm.submit()}
        okText="Переименовать"
      >
        <Form form={renameForm} layout="vertical" onFinish={handleRename}>
          <Form.Item
            name="name"
            label="Новое имя"
            rules={[{ required: true, message: 'Введите новое имя' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно: Публичная ссылка */}
      <Modal
        title="Публичная ссылка"
        open={shareModalOpen}
        onCancel={() => { setShareModalOpen(false); setShareUrl(''); }}
        footer={shareUrl ? [
          <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyLink}>
            Копировать
          </Button>,
          <Button key="close" onClick={() => { setShareModalOpen(false); setShareUrl(''); }}>
            Закрыть
          </Button>,
        ] : [
          <Button key="cancel" onClick={() => { setShareModalOpen(false); setShareUrl(''); }}>
            Отмена
          </Button>,
          <Button
            key="create"
            type="primary"
            onClick={handleCreateShare}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
            }}
          >
            Создать ссылку
          </Button>,
        ]}
      >
        {shareUrl ? (
          <div>
            <Text>Ссылка создана:</Text>
            <Input
              value={shareUrl}
              readOnly
              style={{ marginTop: 8 }}
              addonAfter={<CopyOutlined onClick={handleCopyLink} style={{ cursor: 'pointer' }} />}
            />
          </div>
        ) : (
          <Text>Создать публичную ссылку для "{selectedFile?.name}"?</Text>
        )}
      </Modal>

      {/* Модальное окно: Отправить в чат */}
      <Modal
        title="Отправить в чат"
        open={shareToChatModalOpen}
        onCancel={() => { setShareToChatModalOpen(false); setSelectedRoomId(null); }}
        onOk={handleShareToChat}
        okText="Отправить"
      >
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {chatRooms.map(room => (
            <Card
              key={room.id}
              size="small"
              style={{
                marginBottom: 8,
                cursor: 'pointer',
                border: selectedRoomId === room.id ? '2px solid #6366f1' : '1px solid #d9d9d9',
              }}
              onClick={() => setSelectedRoomId(room.id)}
            >
              <Space>
                <SendOutlined />
                <Text strong>{room.name || 'Чат'}</Text>
              </Space>
            </Card>
          ))}
        </div>
      </Modal>
    </div>
  );
}
